import { useCallback, useEffect, useMemo, useState } from 'react';

export function RuleTestPanel({ workflowName, tenantId, onClose }) {
  const [workflowId, setWorkflowId] = useState('');
  const [testCases, setTestCases] = useState([]);
  const [selectedTestCaseId, setSelectedTestCaseId] = useState('');
  const [testCaseName, setTestCaseName] = useState('');
  const [testCaseDescription, setTestCaseDescription] = useState('');
  const [contextJson, setContextJson] = useState('{\n  \n}');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }), [token]);

  const loadTestCases = useCallback(async (nextWorkflowId) => {
    if (!nextWorkflowId) {
      setTestCases([]);
      return;
    }

    const response = await fetch(`/api/workflows/${nextWorkflowId}/testcases`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await response.json();
    const items = json.data ?? json ?? [];
    setTestCases(Array.isArray(items) ? items.filter(x => x.isActive) : []);
  }, [token]);

  useEffect(() => {
    if (!workflowName) {
      setWorkflowId('');
      setTestCases([]);
      return;
    }

    let cancelled = false;
    fetch('/api/Rules/workflows', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(json => {
        if (cancelled) return;
        const workflows = Array.isArray(json) ? json : [];
        const workflow = workflows.find(x => x.name === workflowName);
        if (!workflow?.id) {
          setWorkflowId('');
          setTestCases([]);
          return;
        }

        setWorkflowId(workflow.id);
        return loadTestCases(workflow.id);
      })
      .catch(() => {
        if (!cancelled) setTestCases([]);
      });

    return () => {
      cancelled = true;
    };
  }, [workflowName, token, loadTestCases]);

  const applyTestCase = (testCaseId) => {
    setSelectedTestCaseId(testCaseId);
    const selected = testCases.find(x => x.id === testCaseId);
    if (!selected) {
      setTestCaseName('');
      setTestCaseDescription('');
      setContextJson('{\n  \n}');
      return;
    }

    setTestCaseName(selected.name || '');
    setTestCaseDescription(selected.description || '');
    setContextJson(selected.inputJson || '{\n  \n}');
    setError('');
    setInfo('');
  };

  const handleTest = async () => {
    let testContext;
    try {
      testContext = JSON.parse(contextJson);
      setError('');
      setInfo('');
    } catch {
      setError('Context gecerli bir JSON degil.');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const res = await fetch('/api/Rules/test', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ workflowName, tenantId, testContext }),
      });
      const json = await res.json();
      if (!res.ok) {
        const failures = json.data?.failures;
        setError(Array.isArray(failures) && failures.length > 0
          ? failures.join('\n')
          : (json.message || 'Test istegi basarisiz.'));
        return;
      }
      setResult(json.data ?? json);
    } catch (ex) {
      setError('Test istegi basarisiz: ' + ex.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTestCase = async () => {
    if (!workflowId) {
      setError('Test case kaydetmek icin workflow kaydi bulunamadi.');
      return;
    }

    if (!testCaseName.trim()) {
      setError('Test case adi zorunludur.');
      return;
    }

    try {
      JSON.parse(contextJson);
      setError('');
      setInfo('');
    } catch {
      setError('Kaydetmeden once gecerli bir JSON girilmelidir.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: testCaseName.trim(),
        description: testCaseDescription.trim(),
        inputJson: contextJson,
        isActive: true,
      };

      const isEdit = !!selectedTestCaseId;
      const url = isEdit
        ? `/api/workflows/${workflowId}/testcases/${selectedTestCaseId}`
        : `/api/workflows/${workflowId}/testcases`;

      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (!res.ok) {
        setError(json.message || 'Test case kaydedilemedi.');
        return;
      }

      await loadTestCases(workflowId);
      setInfo(isEdit ? 'Test case guncellendi.' : 'Test case kaydedildi.');
    } catch (ex) {
      setError('Test case kaydi basarisiz: ' + ex.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTestCase = async () => {
    if (!workflowId || !selectedTestCaseId) {
      setError('Silmek icin once bir test case secin.');
      return;
    }

    setDeleting(true);
    try {
      const res = await fetch(`/api/workflows/${workflowId}/testcases/${selectedTestCaseId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.message || 'Test case silinemedi.');
        return;
      }

      await loadTestCases(workflowId);
      setSelectedTestCaseId('');
      setTestCaseName('');
      setTestCaseDescription('');
      setContextJson('{\n  \n}');
      setResult(null);
      setError('');
      setInfo('Test case silindi.');
    } catch (ex) {
      setError('Test case silme basarisiz: ' + ex.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleNewTestCase = () => {
    setSelectedTestCaseId('');
    setTestCaseName('');
    setTestCaseDescription('');
    setContextJson('{\n  \n}');
    setResult(null);
    setError('');
    setInfo('');
  };

  const handleRunSelectedTestCase = () => {
    if (!selectedTestCaseId) {
      setError('Once bir test case secin.');
      return;
    }

    handleTest();
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>
        <h3>Workflow Test Paneli - <em>{workflowName}</em></h3>
        <p style={styles.note}>
          Bu test secili workflow altindaki tum uygun kurallari birlikte degerlendirir. Tek bir satirdan acilsa bile test kapsami workflow seviyesindedir.
        </p>

        <div style={styles.metaRow}>
          <span style={styles.metaText}>{testCases.length} kayitli test case</span>
          {selectedTestCaseId && (
            <span style={styles.metaText}>Secili kayit guncelleme modunda</span>
          )}
        </div>

        <label>Kayitli Test Case</label>
        <select
          style={styles.input}
          value={selectedTestCaseId}
          onChange={e => applyTestCase(e.target.value)}
        >
          <option value="">Seciniz</option>
          {testCases.map(tc => (
            <option key={tc.id} value={tc.id}>{tc.name}</option>
          ))}
        </select>

        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <input
            style={{ ...styles.input, flex: 1 }}
            placeholder="Test case adi"
            value={testCaseName}
            onChange={e => setTestCaseName(e.target.value)}
          />
          <input
            style={{ ...styles.input, flex: 2 }}
            placeholder="Aciklama"
            value={testCaseDescription}
            onChange={e => setTestCaseDescription(e.target.value)}
          />
        </div>

        <label>Test Context (JSON)</label>
        <textarea
          style={styles.textarea}
          value={contextJson}
          onChange={e => setContextJson(e.target.value)}
          rows={8}
          spellCheck={false}
        />

        {error && <p style={{ color: 'red' }}>{error}</p>}
        {info && <p style={{ color: 'var(--success)' }}>{info}</p>}

        <div style={{ display: 'flex', gap: 8, margin: '12px 0' }}>
          <button onClick={handleNewTestCase} style={styles.btnLight}>
            Yeni Form
          </button>
          <button onClick={handleSaveTestCase} disabled={saving} style={styles.btnSecondary}>
            {saving ? 'Kaydediliyor...' : selectedTestCaseId ? 'Test Case Guncelle' : 'Test Case Kaydet'}
          </button>
          <button onClick={handleDeleteTestCase} disabled={deleting || !selectedTestCaseId} style={styles.btnDanger}>
            {deleting ? 'Siliniyor...' : 'Test Case Sil'}
          </button>
          <button onClick={handleTest} disabled={loading} style={styles.btnPrimary}>
            {loading ? 'Workflow test ediliyor...' : 'Manuel JSON ile Test'}
          </button>
          <button onClick={handleRunSelectedTestCase} disabled={loading || !selectedTestCaseId} style={styles.btnPrimaryAlt}>
            {loading ? 'Calisiyor...' : 'Secili Test Case Calistir'}
          </button>
          <button onClick={onClose} style={styles.btnSecondary}>Kapat</button>
        </div>

        {result && (
          <div>
            <div style={{ marginBottom: 8 }}>
              <strong>Sonuc: </strong>
              <span style={{ color: result.allRulesPassed ? 'green' : 'red', fontWeight: 600 }}>
                {result.allRulesPassed ? 'Tum kurallar gecti' : 'Bazi kurallar basarisiz'}
              </span>
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Kural</th>
                  <th style={styles.th}>Statu</th>
                  <th style={styles.th}>Sonuc</th>
                  <th style={styles.th}>Mesaj</th>
                </tr>
              </thead>
              <tbody>
                {(result.results || []).map((r, i) => (
                  <tr key={i} style={{ background: r.isSuppressed ? '#fff3cd' : r.isSuccess ? '#d4edda' : '#f8d7da' }}>
                    <td style={styles.td}>{r.ruleName}</td>
                    <td style={styles.td}><span style={statusBadgeStyle(r.ruleStatus)}>{r.ruleStatus ?? '-'}</span></td>
                    <td style={styles.td}>{r.isSuppressed ? 'Baskilandi' : r.isSuccess ? 'PASS' : 'FAIL'}</td>
                    <td style={styles.td}>{r.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function statusBadgeStyle(status) {
  const colors = {
    Draft: '#64748b', Testing: 'var(--accent)', PendingApproval: '#fd7e14',
    Active: 'var(--success)', Archived: '#adb5bd',
  };
  return {
    display: 'inline-block', padding: '2px 6px', borderRadius: 4, fontSize: 11,
    background: colors[status] ?? '#dee2e6', color: '#fff', fontWeight: 600,
  };
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  panel: { background: '#fff', borderRadius: 8, padding: 24, width: '90%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto' },
  note: { marginTop: 0, marginBottom: 14, padding: '10px 12px', borderRadius: 8, background: '#fff4db', color: '#7a4a00', fontSize: 13, lineHeight: 1.5 },
  metaRow: { display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 10 },
  metaText: { fontSize: 12, color: '#667085' },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box', marginBottom: 10 },
  textarea: { width: '100%', fontFamily: 'monospace', fontSize: 13, padding: 8, border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box' },
  btnPrimary: { padding: '8px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' },
  btnPrimaryAlt: { padding: '8px 16px', background: 'var(--success)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' },
  btnLight: { padding: '8px 16px', background: '#e9ecef', color: '#212529', border: 'none', borderRadius: 4, cursor: 'pointer' },
  btnSecondary: { padding: '8px 16px', background: '#64748b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' },
  btnDanger: { padding: '8px 16px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '6px 8px', background: '#f1f1f1', border: '1px solid #ccc', textAlign: 'left' },
  td: { padding: '6px 8px', border: '1px solid #ccc' },
};
